import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { getSubscriptionV2, v2UiEnabled } from '@/api/dmpV2.api';
import { getIdentity } from '@/api/dmpIdentity.api';
import useCurrentLang from '@/hooks/useCurrentLang';
import { Panel } from './styles';
import { SubscriptionSurface } from './subscriptionStyles';
import { paidQualificationGuidance, hasSeenGuidance, markGuidanceSeen, onboardingPath } from './qualificationGuidance';

export default function QualificationReminder() {
  const { isLogin, userData } = useAppSelector(getAuthSelector);
  const owner = String(userData?.id || '');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const en = useCurrentLang() === 'en';
  const [notice, setNotice] = useState<ReturnType<typeof paidQualificationGuidance>>(null);
  useEffect(() => {
    let live = true;
    setNotice(null);
    if (
      !isLogin ||
      !owner ||
      !v2UiEnabled() ||
      pathname === '/profile/identity' ||
      pathname === '/profile/plan-purchase' ||
      /^\/(auth|registration|termsUpdate|pbl|certlei|signature-v2|select-application)(\/|$)/.test(pathname)
    )
      return;
    Promise.all([getSubscriptionV2(), getIdentity()])
      .then(([sub, identity]) => {
        if (!live) return;
        const guidance = paidQualificationGuidance(sub.data.data, identity.data.data);
        setNotice(guidance);
        // Never navigate away from an in-progress document, signature or profile form.
        const safeLanding = pathname === '/home' || pathname === '/profile/subscriptionDetail';
        if (
          safeLanding &&
          guidance?.needsInformation &&
          !hasSeenGuidance(owner, guidance.purchase.id, guidance.requestId)
        ) {
          markGuidanceSeen(owner, guidance.purchase.id, guidance.requestId);
          navigate(onboardingPath(guidance.purchase.product_code), { replace: true });
        }
      })
      .catch(() => {
        /* Reminder failure never removes existing access or misreports payment failure. */
      });
    return () => {
      live = false;
    };
  }, [isLogin, owner, pathname, navigate]);
  if (!notice) return null;
  return (
    <SubscriptionSurface>
      <Panel aria-label={en ? 'Qualification reminder' : '資格提醒'}>
        <h2>
          {notice.needsInformation
            ? en
              ? 'Complete your plan information'
              : '繼續完成方案資料'
            : en
            ? 'Qualification review in progress'
            : '資格審核中'}
        </h2>
        <p>
          {en
            ? 'Payment has been received. Your new plan allowances and 12-month term have not started. Existing available benefits remain unchanged.'
            : '付款已完成，新方案額度與 12 個月效期尚未啟用；原有可用權益維持不變。'}
        </p>
        <Link className="action-link" to={onboardingPath(notice.purchase.product_code)}>
          {notice.needsInformation
            ? en
              ? 'Continue with qualification information'
              : '繼續填寫資格資料'
            : en
            ? 'View review progress'
            : '查看審核進度'}
        </Link>
      </Panel>
    </SubscriptionSurface>
  );
}
